import React, { useState } from 'react';
import { Plus, FileText, Trash2, Edit2, Loader, X, Save, AlertTriangle } from 'lucide-react';
import { usePermissions, PERMISSIONS } from './usePermissions';

const PatientNotes = ({ patient, notes, onNotesChange, loading, currentUser }) => {
  const { hasPermission } = usePermissions(currentUser);
  
  const [showAddNote, setShowAddNote] = useState(false);
  const [editingNote, setEditingNote] = useState(null);
  const [noteText, setNoteText] = useState('');
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [errorModal, setErrorModal] = useState(null);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const formatRelativeTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return formatDate(dateString);
  };

  const handleSaveNote = async () => {
    if (!noteText.trim()) return;

    try {
      setSaving(true);
      
      if (editingNote) {
        // Update existing note
        await window.ApiService.updatePatientNote(patient.patientId, editingNote.noteId, {
          noteText: noteText
        });
      } else {
        // Create new note
        await window.ApiService.createPatientNote(patient.patientId, {
          noteText: noteText
        });
      }

      setNoteText('');
      setShowAddNote(false);
      setEditingNote(null);
      onNotesChange(); // Reload notes
    } catch (err) {
      console.error('Error saving note:', err);
      setErrorModal({ title: 'Error Saving Note', message: err.message || 'Failed to save note. Please try again.' });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteNote = async (noteId) => {
    try {
      setSaving(true);
      await window.ApiService.deletePatientNote(patient.patientId, noteId);
      setDeleteConfirm(null);
      onNotesChange(); // Reload notes
    } catch (err) {
      console.error('Error deleting note:', err);
      setErrorModal({ title: 'Error Deleting Note', message: err.message || 'Failed to delete note. Please try again.' });
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (note) => {
    setEditingNote(note);
    setNoteText(note.noteText);
    setShowAddNote(true);
  };

  const cancelEdit = () => {
    setShowAddNote(false);
    setEditingNote(null);
    setNoteText('');
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold text-gray-800">Patient Notes</h3>
        {hasPermission(PERMISSIONS.PATIENTS_NOTES_ADD) && (
          <button
            onClick={() => setShowAddNote(true)}
            className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 flex items-center gap-2"
          >
            <Plus size={18} />
            Add Note
          </button>
        )}
      </div>

      {/* Add/Edit Note Form */}
      {showAddNote && (
        <div className="bg-spruce-50 border-2 border-spruce-200 rounded-lg p-4">
          <div className="flex justify-between items-center mb-3">
            <h4 className="font-semibold text-spruce-900">
              {editingNote ? 'Edit Note' : 'New Note'}
            </h4>
            <button
              onClick={cancelEdit}
              className="text-gray-500 hover:text-gray-700"
            >
              <X size={20} />
            </button>
          </div>
          <textarea
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            placeholder="Enter your note here..."
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-spruce-500 focus:border-spruce-500 min-h-[120px]"
            autoFocus
          />
          <div className="flex gap-2 mt-3">
            <button
              onClick={handleSaveNote}
              disabled={!noteText.trim() || saving}
              className="bg-spruce-600 text-white px-4 py-2 rounded-lg hover:bg-spruce-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {saving ? (
                <>
                  <Loader className="animate-spin" size={16} />
                  Saving...
                </>
              ) : (
                <>
                  <Save size={16} />
                  {editingNote ? 'Update Note' : 'Save Note'}
                </>
              )}
            </button>
            <button
              onClick={cancelEdit}
              disabled={saving}
              className="bg-gray-200 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-300 disabled:opacity-50"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Notes List */}
      {loading ? (
        <div className="text-center py-12 text-gray-500">
          <Loader className="animate-spin mx-auto mb-2" size={32} />
          Loading notes...
        </div>
      ) : notes.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
          <FileText className="mx-auto mb-3 text-gray-400" size={48} />
          <p className="text-gray-600 font-medium">No notes yet</p>
          <p className="text-gray-500 text-sm mt-1">Click "Add Note" to create your first note</p>
        </div>
      ) : (
        <div className="space-y-3">
          {notes.map((note) => (
            <div
              key={note.noteId}
              className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition"
            >
              <div className="flex justify-between items-start mb-2">
                <div className="flex flex-col gap-1">
                  <div className="flex items-center gap-2 text-sm">
                    <FileText size={16} className="text-gray-400" />
                    <span className="font-medium text-gray-700">{note.createdByName || note.createdBy || 'Unknown'}</span>
                    <span className="text-gray-400">•</span>
                    <span className="text-gray-500">{formatRelativeTime(note.createdAt)}</span>
                    {note.updatedAt !== note.createdAt && (
                      <span className="text-xs text-gray-400">(edited)</span>
                    )}
                  </div>
                </div>
                <div className="flex gap-2">
                  {hasPermission(PERMISSIONS.PATIENTS_NOTES_EDIT) && (
                    <button
                      onClick={() => startEdit(note)}
                      className="p-1 text-spruce-600 hover:bg-spruce-50 rounded transition"
                      title="Edit note"
                    >
                      <Edit2 size={16} />
                    </button>
                  )}
                  {hasPermission(PERMISSIONS.PATIENTS_NOTES_DELETE) && (
                    <button
                      onClick={() => setDeleteConfirm(note)}
                      className="p-1 text-red-600 hover:bg-red-50 rounded transition"
                      title="Delete note"
                    >
                      <Trash2 size={16} />
                    </button>
                  )}
                </div>
              </div>
              <p className="text-gray-800 whitespace-pre-wrap">{note.noteText}</p>
              <div className="mt-2 text-xs text-gray-400">
                {formatDate(note.createdAt)}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-xl font-bold text-gray-800 mb-4">Delete Note?</h3>
            <p className="text-gray-600 mb-4">
              Are you sure you want to delete this note? This action cannot be undone.
            </p>
            <div className="bg-gray-50 p-3 rounded-lg mb-4 max-h-32 overflow-y-auto">
              <p className="text-sm text-gray-700 whitespace-pre-wrap">
                {deleteConfirm.noteText}
              </p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => handleDeleteNote(deleteConfirm.noteId)}
                disabled={saving}
                className="flex-1 bg-red-600 text-white py-2 px-4 rounded-lg hover:bg-red-700 disabled:opacity-50 transition flex items-center justify-center gap-2"
              >
                {saving ? (
                  <>
                    <Loader className="animate-spin" size={18} />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 size={18} />
                    Yes, Delete
                  </>
                )}
              </button>
              <button
                onClick={() => setDeleteConfirm(null)}
                disabled={saving}
                className="flex-1 bg-gray-200 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-300 disabled:opacity-50 transition"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Error Modal */}
      {errorModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-3 bg-red-100 rounded-full">
                <AlertTriangle className="text-red-600" size={24} />
              </div>
              <h3 className="text-xl font-bold text-gray-800">{errorModal.title}</h3>
            </div>
            <p className="text-gray-600 mb-6">
              {errorModal.message}
            </p>
            <button
              onClick={() => setErrorModal(null)}
              className="w-full bg-spruce-600 text-white py-2 px-4 rounded-lg hover:bg-spruce-700 transition"
            >
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PatientNotes;